import { ApiError } from './errors';
import { isGuestPath } from '@/lib/auth/guest-paths';
import { redirectIfPlatformMoved } from './platform-moved-client';

export interface ClientApiInit {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  // `unknown` already absorbs `FormData` — the union doesn't widen anything,
  // it's kept here purely as documentation that a `FormData` body is a
  // deliberately supported case (see the `instanceof FormData` branches
  // below), not an oversight.
  body?: unknown;
  searchParams?: Record<string, string>;
  /**
   * Cancels the request: pass React Query's `signal` so a query that is superseded (a new filter)
   * or abandoned (the page left) stops its fetch, and the proxy cancels the backend's work.
   */
  signal?: AbortSignal;
}

/**
 * Shared by every variant: the proxy answers a failure as
 * `{ code, message, details? }` — `details` (the backend's `ValidationDetails`,
 * naming which field failed and why) is only present when the backend sent
 * one (see `[...path]/route.ts`'s own comment on omitting rather than nulling
 * it). Read here and carried onto `ApiError` so a caller can, e.g., land a
 * save-race duplicate-name rejection on the name field instead of a
 * page-level alert — `VALIDATION_FAILED`'s top-level `code`/`message` are both
 * generic ("Bad Request" / "check the highlighted fields"), so `details` is
 * the only place that says WHICH field failed.
 */
async function throwApiError(res: Response): Promise<never> {
  const parsed = await res
    .json()
    .catch(() => ({}) as { code?: string; message?: string; details?: unknown });
  redirectIfPlatformMoved(res.status, parsed.code);
  // Send an expired session back to the login page — but never from a page
  // that is meant to be viewed WITHOUT one. Redirecting to /login from /login
  // reloads the document, which re-issues the request, which 401s again: an
  // infinite reload loop. Every other guest path (forgot-password, sso,
  // enroll-account, the survey links) has the same problem in a form that is
  // harder to spot: the page loads, one incidental authenticated query 401s,
  // and the visitor is thrown to /login before they can read the form. Guard
  // on the whole guest set, not on /login alone.
  if (res.status === 401 && !isGuestPath(window.location.pathname)) {
    window.location.assign('/login');
  }
  throw new ApiError(
    res.status,
    parsed.code ?? String(res.status),
    parsed.message ?? 'Request failed',
    parsed.details,
    // A busy report queue's 503 says when to come back; the proxy passes the header on, and the
    // Change saturation page waits that long before asking again (round 4, R4Q-4).
    res.headers.get('retry-after') ?? undefined,
  );
}

function proxyUrl(path: string, searchParams?: Record<string, string>): string {
  const qs = searchParams ? `?${new URLSearchParams(searchParams)}` : '';
  return `/web-api/proxy/${path.replace(/^\//, '')}${qs}`;
}

export async function clientApi<T>(path: string, init: ClientApiInit = {}): Promise<T> {
  const res = await fetch(proxyUrl(path, init.searchParams), {
    method: init.method ?? 'GET',
    signal: init.signal,
    // FormData must set its own multipart boundary — setting Content-Type by
    // hand produces a body the server cannot parse.
    headers:
      init.body === undefined || init.body instanceof FormData
        ? undefined
        : { 'Content-Type': 'application/json' },
    body:
      init.body === undefined
        ? undefined
        : init.body instanceof FormData
          ? init.body
          : JSON.stringify(init.body),
  });

  if (!res.ok) return throwApiError(res);
  return parseJsonBody<T>(res);
}

/**
 * `res.json()` throws `Unexpected end of JSON input` on an empty body — which
 * a `204 No Content` success response always is (a DELETE is the common
 * case), and any other endpoint is free to send one too. `deleteActivity`
 * hit exactly this: the delete succeeded, but the client's own attempt to
 * parse a body that was never there surfaced as a spurious error toast and
 * left the row stale on screen. Reading `.text()` first and only parsing a
 * non-empty body sidesteps the throw instead of special-casing 204 by status
 * code, which would miss a 200 with an equally empty body.
 */
async function parseJsonBody<T>(res: Response): Promise<T> {
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
}

/**
 * Uploads one file as `multipart/form-data` and returns the JSON reply.
 *
 * No `Content-Type` header: the browser has to set it itself so the multipart
 * boundary matches the body it encodes.
 */
export async function clientApiUpload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const form = new FormData();
  form.append(field, file);

  const res = await fetch(proxyUrl(path), { method: 'POST', body: form });

  if (!res.ok) return throwApiError(res);
  return parseJsonBody<T>(res);
}

/**
 * Fetches a file and returns it with the filename the server chose.
 *
 * The filename comes off `Content-Disposition` rather than being guessed here,
 * so what lands in the user's downloads folder is what the endpoint named it.
 * Saving is left to the caller — this returns the blob so a component can decide
 * whether to save it, preview it, or discard it on unmount.
 */
export async function clientApiDownload(
  path: string,
  fallbackFilename: string,
  // Not every download is a GET: the SSO metadata endpoint issues a new key pair
  // for the company before it can answer, so it is a POST.
  init: Pick<ClientApiInit, 'method'> = {},
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(proxyUrl(path), { method: init.method ?? 'GET' });

  if (!res.ok) return throwApiError(res);

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : fallbackFilename;

  return { blob: await res.blob(), filename };
}

/** Prompts the browser to save a blob under `filename`. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: Safari cancels an in-flight download if the URL
  // is revoked in the same task as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
